import jwtDecode from 'jwt-decode';
import * as Sentry from '@sentry/browser';
import { isServer, isSSOAuthSupported, getRoute, isRoutePublic } from 'utils';
import { User as UserTypes } from 'mxp-schemas';

import { AuthHelper } from './base';
import type { OAuthError } from '@okta/okta-auth-js';
const OKTA_STORAGE_KEY = 'oktaToken';
const USE_PKCE = true;
export const OKTA_POST_LOGIN_REDIRECT = 'okta-post-login-redirect';
export const OKTA_ASSOCIATION_REDIRECT_URL = 'okta-association-redirect-url';
export const OKTA_PRESERVE_POST_SIGNUP_REDIRECT = 'okta-preserve-post-signup-redirect';
type HostedLoginNavigationMode = 'assign' | 'replace';

type HostedLoginRedirectOptions = {
  loginHint?: string;
  action?: 'signup';
  appSource?: string;
  associationRedirectUrl?: string;
  firmBillingEmail?: string;
  firmBillingFirstName?: string;
  firmBillingLastName?: string;
  navigationMode?: HostedLoginNavigationMode;
};

type OktaTokens = {
  accessToken?: {
    accessToken: string;
    expiresAt?: number;
    claims?: OktaProvisioningClaims;
  };
  idToken?: {
    idToken: string;
    expiresAt?: number;
    claims?: OktaProvisioningClaims;
  };
  refreshToken?: {
    refreshToken: string;
    expiresAt?: number;
  };
};

interface OktaJWTClaims {
  uid?: string;
  sub?: string;
  AICPAUID?: string;
  Roles?: string[];
  memberStatus?: string;
  memberType?: string;
  employeeNumber?: string;
  exp?: number;
}

type OktaProvisioningClaims = {
  uid?: string;
  sub?: string;
  email?: string;
  AICPAUID?: string;
  [key: string]: any;
};
export class ConsumerAuthHelper extends AuthHelper {
  /***
   * On some devices SSO is not supported due too cookie sharing issues; disable it and ensure
   * sign is at least possible
   */
  private disableSSO = false;
  private accessTokenRenewalPromise: Promise<string | undefined> | null = null;
  private idTokenRenewalPromise: Promise<string | undefined> | null = null;
  private hostedLoginRedirectInProgress = false;
  private hostedLoginNavigationMode: HostedLoginNavigationMode = 'assign';
  constructor() {
    super(
      OKTA_STORAGE_KEY,
      // using cookie as storage as XSS threat is bigger that XSRF,
      // when using local or session storage any f/e included npm package will
      // have access to storage and, therefor, to your tokens.
      {
        storage: 'cookie',
        secure: isServer || window.location.protocol === 'https:',
        autoRenew: true,
      },
      USE_PKCE,
      false, // useClientIdOverride
      `${window.location.origin}/login/callback`
    );
    if (!isServer) {
      this.authClient.options.setLocation = this.setOktaRedirectLocation.bind(this);
      this.disableSSO = !isSSOAuthSupported();
      setTimeout(() => {
        // essential cleanup activities
        this.cleanupLegacyCookies();
      }, 1);
    }
  }

  private setOktaRedirectLocation(url: string): void {
    const navigationMode = this.hostedLoginNavigationMode;
    this.hostedLoginNavigationMode = 'assign';

    if (navigationMode === 'replace') {
      window.location.replace(url);
      return;
    }

    window.location.assign(url);
  }

  private async generateAccessToken(sessionToken?: any) {
    const authorizationResponse = await this.authClient.token.getWithoutPrompt({
      scopes: ['spa'],
      responseType: ['token'],
      sessionToken,
      pkce: USE_PKCE,
    });
    return authorizationResponse?.tokens?.accessToken;
  }

  async signInUser(email: string, password: string) {
    try {
      const transaction = await this.authClient.signIn({
        username: email,
        password,
      });

      if (
        transaction.status === UserTypes.UserErrorCodes.LOCKED_OUT ||
        transaction.status === UserTypes.UserErrorCodes.PASSWORD_EXPIRED
      ) {
        return transaction;
      }
      if (transaction.status !== 'SUCCESS') {
        throw new Error(`unable to log-in, status: ${transaction.status}`);
      }

      const token = await this.generateAccessToken(transaction.sessionToken);

      this.authClient.tokenManager.add(this.oktaStorageKey, token);
      return transaction;
    } catch (error) {
      try {
        Sentry.captureException(`signInUser failed with error: ${error}`);
      } catch {
        console.error('Sentry failed to capture okta exception');
      }
      return error;
    }
  }

  async redirectToHostedLoginWidget({
    loginHint = '',
    action,
    appSource,
    associationRedirectUrl,
    firmBillingEmail,
    firmBillingFirstName,
    firmBillingLastName,
    navigationMode = 'assign',
  }: HostedLoginRedirectOptions = {}): Promise<void> {
    if (this.hostedLoginRedirectInProgress) {
      return;
    }

    this.hostedLoginRedirectInProgress = true;
    this.hostedLoginNavigationMode = navigationMode;

    try {
      const extraParams: Record<string, string> = Object.fromEntries(
        Object.entries({
          action,
          app_source: appSource,
          association_redirect_url: associationRedirectUrl,
          firmbilling_email: firmBillingEmail,
          firmbilling_first_name: firmBillingFirstName,
          firmbilling_last_name: firmBillingLastName,
        }).filter((entry): entry is [string, string] => Boolean(entry[1]))
      );

      // Initiate Okta-hosted login (with PKCE)
      await this.authClient.token.getWithRedirect({
        scopes: ['openid', 'profile', 'spa', 'offline_access'],
        loginHint,
        extraParams: Object.keys(extraParams).length ? extraParams : undefined,
      });
    } catch (err) {
      this.hostedLoginRedirectInProgress = false;
      this.hostedLoginNavigationMode = 'assign';
      Sentry.captureException(err);
      console.error('Redirect to Okta failed:', err);
      throw err;
    }
  }

  async handleRedirectCallbackAndStoreTokens(): Promise<OktaTokens> {
    try {
      const { tokens } = await this.authClient.token.parseFromUrl();

      if (tokens.accessToken) {
        await this.authClient.tokenManager.add(this.oktaStorageKey, tokens.accessToken);
      }
      if (tokens.idToken) {
        await this.authClient.tokenManager.add('idToken', tokens.idToken);
      }
      if (tokens.refreshToken) {
        await this.authClient.tokenManager.add('refreshToken', tokens.refreshToken);
      }

      return tokens as OktaTokens;
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  }

  async sessionExists() {
    if (isServer) return null;
    try {
      const existingToken = await this.getValidAccessToken();
      if (existingToken) {
        return UserTypes.SessionType.OKTA_SESSION;
      }
      return UserTypes.SessionType.NO_SESSION;
    } catch (error) {
      Sentry.captureException(error);
      // if isAuth === false redirection will be performed by SecureRoute
      return UserTypes.SessionType.NO_SESSION;
    }
  }

  async getUserTokenData(): Promise<{ sub: string } | null> {
    const token: string | undefined = await this.getValidAccessToken();
    if (!token) return null;
    const parsedToken: { sub: string } = jwtDecode(token);
    if (!parsedToken) return null;
    return parsedToken;
  }

  async getProvisioningClaims(): Promise<OktaProvisioningClaims | null> {
    const [accessTokenClaims, idTokenClaims] = await Promise.all([
      this.getStoredTokenClaims(this.oktaStorageKey),
      this.getStoredTokenClaims('idToken'),
    ]);
    const mergedClaims = {
      ...(idTokenClaims ?? {}),
      ...(accessTokenClaims ?? {}),
    };
    if (!Object.keys(mergedClaims).length) return null;
    return mergedClaims;
  }

  async renewProvisioningTokens(): Promise<OktaProvisioningClaims | null> {
    const renewedAccessToken = await this.renewAccessToken();
    if (!renewedAccessToken) return null;

    const renewedIdToken = await this.renewIdToken();
    if (!renewedIdToken) return null;

    return this.getProvisioningClaims();
  }

  async resetPassword(recoveryToken: string, newPassword: string) {
    if (isServer) return null;
    const transaction: any = await this.authClient.verifyRecoveryToken({
      recoveryToken,
    });
    await transaction.resetPassword({
      newPassword,
    });
    return transaction.data?._embedded?.user?.id;
  }

  async getValidAccessToken(): Promise<string | undefined> {
    return this.getValidAccessTokenFromOktaStore();
  }

  async getAccessTokenForOrder(): Promise<string | undefined> {
    return this.renewAccessToken();
  }

  async signOutUser(): Promise<void> {
    return this.signOutUserFromOkta();
  }

  private isTokenExpired(expiresAt?: number): boolean {
    if (typeof expiresAt !== 'number') {
      return true;
    }
    return expiresAt * 1000 <= Date.now();
  }

  private async renewAccessToken(): Promise<string | undefined> {
    this.accessTokenRenewalPromise = this.accessTokenRenewalPromise || this.performAccessTokenRenewal();
    try {
      return await this.accessTokenRenewalPromise;
    } finally {
      this.accessTokenRenewalPromise = null;
    }
  }

  private async performAccessTokenRenewal(): Promise<string | undefined> {
    try {
      const renewedToken = await this.authClient.tokenManager.renew(this.oktaStorageKey);
      if (!renewedToken?.accessToken || this.isTokenExpired(renewedToken.expiresAt)) {
        await this.clearTokens();
        return;
      }
      return renewedToken.accessToken;
    } catch (error) {
      await this.handleRenewalError('access token', error);
      return;
    }
  }

  private async renewIdToken(): Promise<string | undefined> {
    this.idTokenRenewalPromise = this.idTokenRenewalPromise || this.performIdTokenRenewal();
    try {
      return await this.idTokenRenewalPromise;
    } finally {
      this.idTokenRenewalPromise = null;
    }
  }

  private async performIdTokenRenewal(): Promise<string | undefined> {
    try {
      const renewedToken: any = await this.authClient.tokenManager.renew('idToken');
      if (!renewedToken?.idToken || this.isTokenExpired(renewedToken.expiresAt)) {
        await this.clearTokens();
        return;
      }
      return renewedToken.idToken;
    } catch (error) {
      await this.handleRenewalError('id token', error);
      return;
    }
  }

  private async getStoredTokenClaims(tokenKey: string): Promise<OktaProvisioningClaims | null> {
    try {
      const token: { claims?: OktaProvisioningClaims; accessToken?: string; idToken?: string } | undefined =
        await this.authClient.tokenManager.get(tokenKey);
      if (!token) return null;
      if (token.claims) return token.claims;

      const rawToken = token.accessToken ?? token.idToken;
      if (!rawToken) return null;
      return jwtDecode(rawToken) as OktaProvisioningClaims;
    } catch (error) {
      Sentry.captureException(error);
      return null;
    }
  }

  private async handleRenewalError(tokenType: string, error: unknown): Promise<void> {
    if (!((error as OAuthError)?.errorCode === 'invalid_grant')) {
      console.error(`Error renewing ${tokenType}:`, error);
      Sentry.captureException(error);
    }
    await this.clearTokens();
    // On public routes the user can keep browsing unauthenticated; protected
    // routes are still funnelled to the widget by SecureRoute once isAuth resolves.
    if (this.isCurrentRoutePublic()) {
      return;
    }
    sessionStorage.setItem(
      OKTA_POST_LOGIN_REDIRECT,
      `${window.location.pathname || '/'}${window.location.search ?? ''}${window.location.hash ?? ''}`
    );
    await this.redirectToHostedLoginWidget();
  }

  private isCurrentRoutePublic(): boolean {
    if (isServer) return false;
    const currentRoute = getRoute(window.location.pathname || '/', false);
    return Boolean(currentRoute && isRoutePublic(currentRoute));
  }

  protected async getValidAccessTokenFromOktaStore(): Promise<string | undefined> {
    let lastError: unknown;
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const token = await this.authClient.tokenManager.get(this.oktaStorageKey);
        if (token?.accessToken) {
          if (this.isTokenExpired(token.expiresAt)) {
            return this.renewAccessToken();
          }
          return token.accessToken;
        }

        return this.getAccessTokenFromCookie();
      } catch (error) {
        lastError = error;
        if (attempt < 2) {
          await new Promise(resolve => setTimeout(resolve, 300));
        }
      }
    }

    if (lastError) {
      Sentry.captureException(lastError);
    }

    return this.getAccessTokenFromCookie();
  }

  private getAccessTokenFromCookie(): string | undefined {
    if (isServer) return undefined;
    try {
      const cookieName = `okta-token-storage_${this.oktaStorageKey}`;
      const cookieValue = document.cookie
        .split(';')
        .map(c => c.trim())
        .find(row => row.startsWith(`${cookieName}=`))
        ?.split('=')
        .slice(1)
        .join('=');
      if (!cookieValue) return undefined;

      const parsed = JSON.parse(decodeURIComponent(cookieValue));
      const accessToken = parsed?.accessToken;

      if (!accessToken || typeof accessToken !== 'string') return undefined;

      const parts = accessToken.split('.');
      if (parts.length !== 3) return undefined;

      const claims = this.parseJWTClaims(accessToken);
      if (this.isTokenExpired(claims.exp)) return undefined;

      return accessToken;
    } catch (error) {
      Sentry.captureException(error);
      return undefined;
    }
  }

  private parseJWTClaims(accessToken: string): OktaJWTClaims {
    try {
      const parts = accessToken.split('.');
      if (parts.length !== 3) return {};
      const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
      const payload = JSON.parse(atob(padded));
      return payload as OktaJWTClaims;
    } catch {
      return {};
    }
  }

  async signOutUserFromOkta(): Promise<void> {
    if (isServer) return;
    try {
      const accessToken = await this.authClient.tokenManager.get(this.oktaStorageKey);

      await this.authClient.signOut({
        clearTokensBeforeRedirect: true,
        postLogoutRedirectUri: window.location.origin,
        accessToken,
      });
    } catch (error) {
      Sentry.captureException(error);
      console.error('Logout failed, forcing local cleanup:', error);
      // even if signOut fails (e.g. network error), we should clear tokens
      // and redirect to avoid leaving user in a broken state
      await this.clearTokens();
      window.location.replace(window.location.origin);
    }
  }
}
